import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Trash2, PackagePlus, Plus } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { PageHeader } from "../components/Bits";
import { ProductPicker } from "./NewOrderPage";
import { SupplierFormDialog } from "../components/SupplierDialogs";
import { api, errMsg, money } from "../lib/api";

const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };

const Line = ({ l, upd, del }) => (
  <div data-testid={`purchase-line-${l.code}`} className="py-3">
    <div className="flex justify-between gap-2">
      <div className="min-w-0"><p className="truncate text-sm font-semibold">{l.name}</p><p className="font-mono text-[11px] text-slate-500">{l.code} · Mevcut stok {l.stock} {l.unit}</p></div>
      <button data-testid={`purchase-line-remove-${l.code}`} onClick={del} className="text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>
    </div>
    <div className="mt-2 grid grid-cols-4 items-end gap-2">
      <div><Label className="text-[10px] uppercase text-slate-500">Miktar</Label><Input data-testid={`purchase-qty-${l.code}`} type="number" min="0.01" step="1" value={l.quantity} onChange={(e) => upd({ quantity: e.target.value })} className="h-9" /></div>
      <div><Label className="text-[10px] uppercase text-slate-500">Alış ₺</Label><Input data-testid={`purchase-cost-${l.code}`} type="number" min="0" step="0.01" value={l.unit_cost} onChange={(e) => upd({ unit_cost: e.target.value })} className="h-9" /></div>
      <div><Label className="text-[10px] uppercase text-slate-500">KDV %</Label><Input data-testid={`purchase-vat-${l.code}`} type="number" min="0" max="100" value={l.vat} onChange={(e) => upd({ vat: e.target.value })} className="h-9" /></div>
      <p className="pb-2 text-right font-display text-sm font-bold">{money((Number(l.quantity) || 0) * (Number(l.unit_cost) || 0))}</p>
    </div>
  </div>
);

export default function NewPurchasePage() {
  const nav = useNavigate();
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [lines, setLines] = useState([]);
  const [h, setH] = useState({ supplier_id: "", invoice_no: "", invoice_date: today(), note: "" });
  const [supOpen, setSupOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const loadSup = () => api.get("/suppliers").then((r) => setSuppliers(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { api.get("/products").then((r) => setProducts(r.data)).catch((e) => toast.error(errMsg(e))); loadSup(); }, []);

  const add = (p) => setLines((ls) => ls.some((l) => l.product_id === p.id) ? ls.map((l) => l.product_id === p.id ? { ...l, quantity: Number(l.quantity) + 1 } : l)
    : [...ls, { product_id: p.id, code: p.code, name: p.name, unit: p.unit, stock: p.stock, quantity: 1, unit_cost: p.cost_price || "", vat: p.vat }]);
  const upd = (id, patch) => setLines((ls) => ls.map((l) => (l.product_id === id ? { ...l, ...patch } : l)));
  const sub = lines.reduce((a, l) => a + (Number(l.quantity) || 0) * (Number(l.unit_cost) || 0), 0);
  const vat = lines.reduce((a, l) => a + (Number(l.quantity) || 0) * (Number(l.unit_cost) || 0) * (Number(l.vat) || 0) / 100, 0);

  const save = async () => {
    if (!h.supplier_id) return toast.error("Tedarikçi seçin");
    if (!lines.length) return toast.error("En az bir ürün ekleyin");
    if (lines.some((l) => !(Number(l.quantity) > 0) || l.unit_cost === "" || Number(l.unit_cost) < 0)) return toast.error("Miktar ve alış fiyatlarını kontrol edin");
    setBusy(true);
    try {
      const { data } = await api.post("/purchases", { ...h, items: lines.map((l) => ({ product_id: l.product_id, quantity: Number(l.quantity), unit_cost: Number(l.unit_cost), vat: Number(l.vat) || 0 })) });
      toast.success(`${data.purchase_no} kaydedildi, stok girişi yapıldı`);
      nav("/alislar");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader title="Yeni Alış Faturası" kicker="Ürün seç · Alış fiyatı gir · Stok girişi">
        <Button data-testid="purchase-back-button" variant="outline" onClick={() => nav("/alislar")}><ArrowLeft className="mr-2 h-4 w-4" />Alışlar</Button>
      </PageHeader>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <div className="lg:col-span-6"><ProductPicker products={products} onAdd={(p) => { add(p); toast.success(`${p.name} eklendi`); }} /></div>
        <div className="space-y-4 lg:col-span-6">
          <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="font-display text-lg font-semibold">Fatura Bilgileri</h2>
            <div className="flex gap-2">
              <Select value={h.supplier_id} onValueChange={(v) => setH({ ...h, supplier_id: v })}>
                <SelectTrigger data-testid="purchase-supplier-select" className="flex-1"><SelectValue placeholder="Tedarikçi seçin *" /></SelectTrigger>
                <SelectContent className="bg-white">{suppliers.map((s) => <SelectItem key={s.id} value={s.id} data-testid={`purchase-supplier-option-${s.id}`}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
              <Button data-testid="purchase-new-supplier-button" type="button" variant="outline" onClick={() => setSupOpen(true)}><Plus className="h-4 w-4" /></Button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input data-testid="purchase-invoice-no-input" placeholder="Tedarikçi fatura no" value={h.invoice_no} onChange={(e) => setH({ ...h, invoice_no: e.target.value })} />
              <Input data-testid="purchase-invoice-date-input" type="date" value={h.invoice_date} onChange={(e) => setH({ ...h, invoice_date: e.target.value })} />
            </div>
            <Textarea data-testid="purchase-note-input" placeholder="Not" value={h.note} onChange={(e) => setH({ ...h, note: e.target.value })} />
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <h2 className="font-display text-lg font-semibold">Ürünler <span className="text-slate-400">({lines.length})</span></h2>
            {lines.length === 0 ? <p data-testid="purchase-lines-empty" className="py-8 text-center text-sm text-slate-500">Soldan ürün arayıp ekleyin.</p>
              : <div className="divide-y divide-slate-100">{lines.map((l) => <Line key={l.product_id} l={l} upd={(p) => upd(l.product_id, p)} del={() => setLines((ls) => ls.filter((x) => x.product_id !== l.product_id))} />)}</div>}
          </div>
          <div className="space-y-1 rounded-xl bg-slate-950 p-5 text-white">
            <div className="flex justify-between text-sm text-slate-300"><span>Ara Toplam</span><span data-testid="purchase-subtotal">{money(sub)}</span></div>
            <div className="flex justify-between text-sm text-slate-300"><span>KDV</span><span data-testid="purchase-vat">{money(vat)}</span></div>
            <div className="flex justify-between border-t border-slate-800 pt-2 font-display text-lg font-bold"><span>Genel Toplam</span><span data-testid="purchase-grand-total">{money(sub + vat)}</span></div>
            <Button data-testid="purchase-submit-button" disabled={busy} onClick={save} className="mt-3 h-12 w-full bg-blue-600 text-base hover:bg-blue-500"><PackagePlus className="mr-2 h-4 w-4" />{busy ? "Kaydediliyor..." : "Kaydet ve Stoğa Ekle"}</Button>
          </div>
        </div>
      </div>
      <SupplierFormDialog open={supOpen} onOpenChange={setSupOpen} supplier={null} onSaved={(s) => { loadSup(); setH((x) => ({ ...x, supplier_id: s.id })); }} />
    </div>
  );
}
